// `/signin`, WITH THE SERVER'S WEB_ACCOUNTS_ENABLED ON (ACC-3a; ACCOUNTS_PLAN §3 B0–B9).
//
// In the app's order: the heading and its line (B1, B2, the app's own words from ProfileTab.swift); B7 when this tab is
// holding a look-around's unpaid appeal; "Continue with Apple" first (B0) with B8 under it; "or use your email" (A3); the
// email and the password (B3), "Sign in" (B4), "Forgot your password?" (B5), "New to PROppeal? Create an account" (B6);
// B9 below the form.
//
// ONE SENTENCE FOR A WRONG PASSWORD AND FOR NO ACCOUNT (C1). The seam answers both as `wrongPasswordOrNoAccount`, and
// `refusalCopy.ts` gives that one sentence. Nothing on this page differs between the two.
//
// WHERE AN OWNER GOES AFTER. Back where they were (`from`), after the look-around is dealt with (`finish.ts`, B7). ONE
// EXCEPTION: "Continue with Apple" with an Apple ID that has no account yet CREATES one — the seam links the look-around
// to it — and nobody has ticked the terms box on this page, so the owner is sent to `/account`, which for an account is
// the assent page, and the terms are agreed there before paying.
//
// B7 IS THE SERVER'S ANSWER, NOT A GUESS: it shows only when this browser is a look-around AND the appeal this tab holds
// is on its account and unpaid — `v2Outcomes` `list`'s snapshot of it (`finish.ts` `useUnpaidLookAround`) — and the
// promise it makes is kept by `finish.ts` on exactly that condition.

import { useId, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import type { AuthRefusal } from "../../services";
import { signedIn } from "../../app/accounts";
import { useSession } from "../../app/session";
import { heldText } from "../../components/heldCopy";
import { accountState, useFinishAccountStep, useUnpaidLookAround } from "./finish";
import {
  AccountForm, AppleButton, AuthError, Divider, EmailField, HeroTile, PasswordField, Say, SayWithLink, SubmitButton,
} from "./parts";
import { looksLikeEmail } from "./refusalCopy";

export function SignIn() {
  const session = useSession();
  const location = useLocation();
  const finish = useFinishAccountStep();
  const arrived = accountState(location.state);
  const to = arrived.from ?? "/";
  // Decided once, on arrival: an owner already signed in has nothing to do here. A sign-in on this page changes the
  // answer, and must not bounce the page before it has finished.
  const [arrivedSignedIn] = useState(() => signedIn(session.auth));

  const [email, setEmail] = useState(arrived.email ?? "");
  const [password, setPassword] = useState("");
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [working, setWorking] = useState<"apple" | "password" | null>(null);
  const headingId = useId();

  const unpaidLookAround = useUnpaidLookAround();

  if (arrivedSignedIn) return <Navigate to={to} replace />;

  const run = async (how: "apple" | "password") => {
    if (working !== null) return;
    setRefusal(null);
    setWorking(how);
    try {
      if (how === "password" && !looksLikeEmail(email)) return setRefusal({ kind: "notAnEmail" });
      const result = how === "apple"
        ? await session.auth.continueWithApple()
        : await session.auth.signInWithPassword(email.trim(), password);
      if (!result.ok) return setRefusal(result.refusal);
      // A NEW Apple account, made by linking: its terms have not been agreed on this page (see the header).
      const created = how === "apple" && result.lookAround.kind === "carriedOver";
      await finish(result, created ? "/account" : to, unpaidLookAround);
    } finally {
      setWorking(null);
    }
  };

  return (
    <section className="account-page" aria-labelledby={headingId} data-account-screen="signin">
      <HeroTile />
      <h1 id={headingId} data-held-copy="account.signIn.heading">{heldText("account.signIn.heading")}</h1>
      <Say id="account.signIn.lead" className="account-lead" />

      {unpaidLookAround && <Say id="account.signIn.lookAround" className="note warn account-note" />}

      <AppleButton
        copyId="account.signIn.apple"
        disabled={working !== null && working !== "apple"}
        busy={working === "apple"}
        onClick={() => void run("apple")}
      />
      <Say id="account.signIn.appleInApp" className="faint account-under-apple" />

      <Divider />

      <AccountForm onSubmit={() => void run("password")}>
        <EmailField value={email} onChange={setEmail} invalid={refusal?.kind === "notAnEmail"} />
        <PasswordField value={password} onChange={setPassword} autoComplete="current-password" />
        <AuthError refusal={refusal} />
        <SubmitButton id="account.signIn.button" busy={working === "password"} disabled={working !== null && working !== "password"} />
      </AccountForm>

      <p className="account-links">
        {/* In-app, carrying what was typed so the owner does not type it twice. */}
        <Link to="/forgot" state={{ email: email.trim(), from: arrived.from }} data-held-copy="account.signIn.forgot">
          {heldText("account.signIn.forgot")}
        </Link>
      </p>
      <SayWithLink id="account.signIn.newHere" to="/account" state={{ from: arrived.from }} className="account-switch" />
      <Say id="account.signIn.appCredits" className="faint account-small" />
    </section>
  );
}
